import React, {FC, useEffect} from 'react';
import {QueryClient, QueryClientProvider} from 'react-query';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {useInitialEffect} from '../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {usePrevious} from '../../srcCommon/helpers/hookHelpers';
import {Redirect} from '../components/common/redirect';
import {inLegacyTransientPanel} from '../components/legacyTransientPanel';
import {ResolveDispatchProps, useAppSelector} from '../helpers/reduxHelpers';
import {
  createDeepEqualSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../helpers/reselectHelpers';
import {CustomFieldResourceTypesEnum} from '../models/api/customFieldModel';
import {
  parseSettingsUrl,
  SettingsMatchProps,
  SettingsRootPagesEnum,
  SettingsUrlModel,
} from '../models/url/settingsUrlModel';
import {AppState} from '../store/appState';
import {fetchCustomFieldsForResourceTypeAsyncAction} from '../store/data/customFields/customFieldsActions';
import {getIsUserGlobalAdmin, getUserNamespace} from '../store/data/teammates/teammatesSelectors';
import {getFirstTeamUserIsAdminOf} from '../store/data/teams/teamsSelectors';
import {getHasNewContactsManagerAccounts} from '../store/featureFlags/featureFlagsSelectors';
import {SettingsPanelId} from '../store/panels/panelStateBase';
import {navigateSettingsAction, openSettingsPageAction} from '../store/panels/settings/settingsPanelActions';
import {SettingsAnswersContainer} from './answers/settingsAnswersContainer';
import {SettingsAppsContainer} from './apps/settingsAppsContainer';
import {SettingsCircles} from './circles/settingsCircles';
import {SettingsCustomFieldsContainer} from './customFields/settingsCustomFieldsContainer';
import {SettingsDevelopersContainer} from './developers/settingsDevelopersContainer';
import {SettingsHomeContainer} from './home/settingsHomeContainer';
import {SettingsInboxes} from './inboxes/settingsInboxes';
import {SettingsNotificationsContainer} from './notifications/settingsNotificationsContainer';
import {SettingsPreferencesContainer} from './preferences/settingsPreferencesContainer';
import {SettingsProfile} from './profile/settingsProfile';
import {SettingsRulesContainer} from './rules/settingsRulesContainer';
import {SettingsSignaturesContainer} from './signatures/settingsSignaturesContainer';
import {SettingsSnoozesContainer} from './snoozes/settingsSnoozesContainer';
import {SettingsTagsContainer} from './tags/settingsTagsContainer';
import {SettingsTeammatesContainer} from './teammates/settingsTeammatesContainer';
import {SettingsApplicationsContainer} from './tools/applications/settingsApplicationsContainer';
import {SettingsVacationRespondersContainer} from './vacationResponders/settingsVacationRespondersContainer';

/*
 * Constants.
 */

const QUERY_CLIENT = new QueryClient();

/*
 * Props.
 */

interface SettingsContainerPublicProps {
  panelId: SettingsPanelId;
  matchProps: SettingsMatchProps;
}

interface SettingsContainerProps
  extends SettingsContainerPublicProps,
    ResolveStateProps<typeof mapStateToProps>,
    ResolveDispatchProps<typeof mapDispatchToProps> {}

/*
 * Component.
 */

const SettingsContainerComponent: FC<SettingsContainerProps> = (props) => {
  const {panelId, url, fetchCustomFieldsForResourceTypeAsync, openSettingsPage, userDefaultSettingsPath} =
    props;
  const hasNewContactsManagerAccounts = useAppSelector(getHasNewContactsManagerAccounts);
  const previousRootPage = usePrevious(url?.rootPage);

  useInitialEffect(() => {
    if (hasNewContactsManagerAccounts) {
      fetchCustomFieldsForResourceTypeAsync(CustomFieldResourceTypesEnum.ACCOUNT);
    }
  });

  // Watch the root page and open the page.
  useEffect(() => {
    if (url?.rootPage && url?.rootPage !== previousRootPage) {
      openSettingsPage(url.rootPage);
    }
  }, [openSettingsPage, previousRootPage, url?.rootPage]);

  function redirectToUserHome() {
    return <Redirect panelId={panelId} to={userDefaultSettingsPath} />;
  }

  const settingsComponent = url && getSettingsComponent(panelId, url);

  if (!settingsComponent) {
    return redirectToUserHome();
  }

  return <QueryClientProvider client={QUERY_CLIENT}>{settingsComponent}</QueryClientProvider>;
};

/*
 * Helpers.
 */

function getSettingsComponent(panelId: SettingsPanelId, url: SettingsUrlModel) {
  switch (url.rootPage) {
    case SettingsRootPagesEnum.TAGS: {
      return <SettingsTagsContainer panelId={panelId} url={url} />;
    }

    case SettingsRootPagesEnum.RULES: {
      return <SettingsRulesContainer panelId={panelId} url={url} />;
    }

    case SettingsRootPagesEnum.TEAMMATES:
      return <SettingsTeammatesContainer panelId={panelId} url={url} />;

    case SettingsRootPagesEnum.CUSTOM_FIELDS: {
      return <SettingsCustomFieldsContainer url={url} />;
    }

    case SettingsRootPagesEnum.SIGNATURES: {
      return <SettingsSignaturesContainer panelId={panelId} url={url} />;
    }

    case SettingsRootPagesEnum.ANSWERS: {
      return <SettingsAnswersContainer panelId={panelId} url={url} />;
    }

    case SettingsRootPagesEnum.INBOXES: {
      return <SettingsInboxes panelId={panelId} url={url} />;
    }

    case SettingsRootPagesEnum.USER_PREFERENCES: {
      return <SettingsPreferencesContainer panelId={panelId} url={url} />;
    }

    case SettingsRootPagesEnum.SNOOZES: {
      return <SettingsSnoozesContainer panelId={panelId} url={url} />;
    }

    case SettingsRootPagesEnum.APPLICATIONS: {
      return <SettingsApplicationsContainer panelId={panelId} url={url} />;
    }

    case SettingsRootPagesEnum.CIRCLES: {
      return <SettingsCircles panelId={panelId} url={url} />;
    }

    case SettingsRootPagesEnum.ADMIN_HOME:
    case SettingsRootPagesEnum.USER_HOME: {
      return <SettingsHomeContainer panelId={panelId} url={url} />;
    }

    case SettingsRootPagesEnum.PROFILE: {
      return <SettingsProfile panelId={panelId} url={url} />;
    }

    case SettingsRootPagesEnum.APPS: {
      return <SettingsAppsContainer panelId={panelId} url={url} />;
    }

    case SettingsRootPagesEnum.VACATION_RESPONDERS: {
      return <SettingsVacationRespondersContainer panelId={panelId} url={url} />;
    }

    case SettingsRootPagesEnum.NOTIFICATIONS: {
      return <SettingsNotificationsContainer panelId={panelId} url={url} />;
    }

    case SettingsRootPagesEnum.DEVELOPERS: {
      return <SettingsDevelopersContainer panelId={panelId} url={url} />;
    }

    default:
      return null;
  }
}

/*
 * Selectors.
 */

function makeGetSettingsMatchProps() {
  return createDeepEqualSelector(
    (_state: AppState, props: SettingsContainerPublicProps) => props.matchProps,
    (r) => r,
  );
}

function makeGetSettingsUrl() {
  return createSelector(
    [makeGetSettingsMatchProps(), getUserNamespace],
    (settingsMatchProps, userNamespace) => parseSettingsUrl(settingsMatchProps, userNamespace),
  );
}

export const getUserDefaultSettingsPath = createSelector(
  [getIsUserGlobalAdmin, getFirstTeamUserIsAdminOf],
  (userIsGlobalAdmin, firstTeamUserIsAdminOf) =>
    userIsGlobalAdmin || firstTeamUserIsAdminOf ? '/settings/home' : '/settings/user-home',
);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<SettingsContainerPublicProps>()(() => ({
  url: makeGetSettingsUrl(),
  userDefaultSettingsPath: getUserDefaultSettingsPath,
}));

const mapDispatchToProps = makeMapDispatchToProps<SettingsContainerPublicProps>()(() => ({
  fetchCustomFieldsForResourceTypeAsync: fetchCustomFieldsForResourceTypeAsyncAction,
  openSettingsPage: openSettingsPageAction,
}));

export const SettingsContainer = inLegacyTransientPanel(
  connect(mapStateToProps, mapDispatchToProps)(SettingsContainerComponent),
  ({panelId}) => navigateSettingsAction(panelId),
);
